/*
 * Productos por pestañas (sections/enova-product-tabs.liquid).
 * - Pestañas: muestran su carrusel y su «Ver todo»; flechas del teclado entre pestañas. Una marca
 *   corta bajo la activa viaja de una palabra a otra.
 * - Flechas: avanzan una tarjeta; la de un extremo se atenúa.
 * - Progreso: la línea inferior marca qué parte del carrusel se ve. Donde el navegador lo permite la
 *   mueve el propio desplazamiento (CSS, fuera del hilo principal); si no, se mueve aquí, cuadro a cuadro.
 * - Cambio de pestaña: los recuadros y divisores quedan quietos y el contenido cambia con un fundido
 *   suave (4 px de desplazamiento, todo a la vez). La primera vez que la sección se ve, la fila
 *   aparece igual.
 * - Fluidez: con la página ya cargada (o al apuntar a una pestaña) cada panel se maqueta invisible,
 *   se mide y pide sus fotos al tamaño final. Así el cambio de pestaña solo muestra y anima, y al
 *   deslizar no se recalcula nada ni se vuelven a pedir fotos.
 */
if (!customElements.get('enova-product-tabs')) {
  const ROW = '[data-enova-tabs-row]';
  // Lo que cambia dentro de cada tarjeta al pasar de pestaña; el recuadro gris y el divisor quedan
  const TEXT = '.m-product-card__content';
  const PARTS = `.m-product-card__main-image .m-image, .m-product-card__tags, ${TEXT}`;
  const scrollDriven = window.CSS && CSS.supports('(animation-timeline: --a) and (timeline-scope: --a)');

  class EnovaProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-enova-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-enova-tab-panel]'));
      this.rows = this.panels.map((panel) => panel.querySelector(ROW));
      this.bars = this.panels.map((panel) => panel.querySelector('[data-enova-tabs-bar]'));
      this.links = Array.from(this.querySelectorAll('[data-enova-tab-link]'));
      this.prev = this.querySelector('[data-enova-tabs-prev]');
      this.next = this.querySelector('[data-enova-tabs-next]');
      this.list = this.querySelector('.enova-tabs__list');
      this.mark = this.querySelector('[data-enova-tabs-mark]');
      if (!this.tabs.length) return;

      this.abort = new AbortController();
      const { signal } = this.abort;
      this.sync = this.sync.bind(this);

      this.tabs.forEach((tab, index) => {
        // Con mouse se activa al presionar (no espera a soltar); en táctil, con el toque normal,
        // para no confundirlo con deslizar la fila de pestañas. El teclado usa click.
        tab.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button === 0) this.select(index);
        }, { signal });
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const step = event.key === 'ArrowRight' ? 1 : -1;
          const target = (index + step + this.tabs.length) % this.tabs.length;
          this.select(target);
          this.tabs[target].focus();
        }, { signal });
      });

      // Un solo cálculo por cuadro, y solo lee la posición (las medidas ya están guardadas)
      this.rows.forEach((row) => {
        row?.addEventListener('scroll', () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = 0;
            this.sync();
          });
        }, { passive: true, signal });
      });

      this.initWheelLock(signal);

      this.prev?.addEventListener('click', () => this.scrollByCard(-1), { signal });
      this.next?.addEventListener('click', () => this.scrollByCard(1), { signal });
      window.addEventListener('resize', () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.measure(this.panels));
      }, { signal });

      this.active = 0;
      this.sorting = this.rows.map((row) => this.sortRow(row, signal));
      this.measure([this.panels[0]]);
      this.initMark();
      this.initEntrance();
      this.preparePanels(signal);

      // Editor de temas: seleccionar un bloque abre su pestaña
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index > -1) this.select(index);
      }, { signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    // El carrusel solo responde a gestos horizontales. Con trackpad, bajar la página casi siempre
    // trae algo de desplazamiento lateral: sobre el carrusel eso lo corría de costado (y luego se
    // reacomodaba a la tarjeta). Cada gesto se clasifica por su primer movimiento; si es vertical,
    // hasta que termina (una pausa de 180 ms) solo baja o sube la página y el carrusel no se mueve.
    // Los gestos horizontales (y Mayús + rueda) siguen igual, con el desplazamiento nativo.
    initWheelLock(signal) {
      let axis = null;
      let timer = 0;
      const unit = (event) => (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1);
      this.rows.forEach((row) => {
        row?.addEventListener('wheel', (event) => {
          if (event.ctrlKey) return;
          if (!axis) axis = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? 'y' : 'x';
          clearTimeout(timer);
          timer = setTimeout(() => {
            axis = null;
          }, 180);
          if (axis !== 'y' || event.deltaX === 0) return;
          event.preventDefault();
          window.scrollBy({ top: event.deltaY * unit(event), behavior: 'instant' });
        }, { passive: false, signal });
      });
    }

    // Primera vez en pantalla: si la sección arranca fuera de la vista, sus tarjetas esperan
    // ocultas y entran al aparecer. Si ya se ve al cargar, no se anima (evita un parpadeo).
    // Con «Más vendidos», la entrada espera a que lleguen las tarjetas en ese orden; si la sección
    // ya se ve al cargar, las tarjetas entran recién cuando llegan (antes quedan ocultas).
    initEntrance() {
      const start = this.active;
      const sorted = this.sorting[start];
      const reveal = (animate) => {
        if (this.active !== start) return;
        const row = this.rows[start];
        this.measure([this.panels[start]]);
        row?.classList.remove('is-sorting');
        if (animate) this.slideIn(this.panels[start], null, 1);
      };
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || !('IntersectionObserver' in window)) {
        sorted.then(() => reveal(false));
        return;
      }
      if (this.getBoundingClientRect().top < window.innerHeight) {
        const waiting = this.rows[start]?.classList.contains('is-sorting');
        sorted.then(() => reveal(waiting));
        return;
      }
      this.classList.add('is-waiting');
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        // En el mismo paso: la animación arranca desde opacidad 0, sin un cuadro visible antes
        sorted.then(() => {
          this.classList.remove('is-waiting');
          reveal(true);
        });
      }, { rootMargin: '0px 0px -15% 0px' });
      observer.observe(this);
      this.abort.signal.addEventListener('abort', () => observer.disconnect());
    }

    // Movimiento mínimo, sin coreografía: todo el contenido cambia a la vez con un fundido suave y un
    // desplazamiento de apenas 4 px hacia el lado de la pestaña elegida (sin tarjetas en ola ni
    // textos desfasados). Primera vez en pantalla (sin panel anterior): la fila aparece igual.
    // Cambio de pestaña: los recuadros grises y los divisores no se mueven y solo cambia lo que
    // llevan dentro (foto, etiquetas y textos): lo anterior se desvanece (0,16 s) y lo nuevo
    // aparece (0,45 s tras 0,09 s), mientras la marca viaja a la nueva pestaña. Así nunca se ven
    // dos carruseles superpuestos ni el gris se aclara a mitad del cambio.
    // Todo con transform y opacidad, que anima la GPU. El panel que sale pasa a hidden cuando el
    // nuevo termina de entrar, y la limpieza de las animaciones espera a que el navegador esté libre.
    slideIn(next, prev, dir, onSettled) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const nextRow = next.querySelector(ROW);
      const prevRow = prev && prev !== next ? prev.querySelector(ROW) : null;
      if (reduced || !nextRow) {
        if (prev && prev !== next) prev.hidden = true;
        onSettled?.();
        return;
      }

      const transition = (this.transitionId = (this.transitionId || 0) + 1);
      const running = (this.running = []);
      const soft = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
      const enterFrames = [{ opacity: 0, transform: `translate3d(${dir * 4}px, 0, 0)` }, { opacity: 1, transform: 'translate3d(0, 0, 0)' }];
      const enter = { duration: 450, delay: 90, easing: soft, fill: 'backwards' };

      if (!prevRow) {
        nextRow.style.willChange = 'transform, opacity';
        running.push(nextRow.animate(enterFrames, { duration: 600, easing: soft, fill: 'backwards' }));
      } else {
        prev.classList.add('is-leaving');
        next.classList.add('is-entering');
        const leaveFrames = [{ opacity: 1 }, { opacity: 0 }];
        const leave = { duration: 160, easing: 'ease-out', fill: 'forwards' };
        const prevItems = this.visibleItems(prevRow);
        const nextItems = this.visibleItems(nextRow);

        // Si la fila anterior quedó a media tarjeta (al final del carrusel), sus recuadros no
        // coinciden con los nuevos: entonces se desvanece entera
        if (prevItems.length && this.tilesAligned(prevRow)) {
          prevItems.forEach((item) => item.querySelectorAll(PARTS).forEach((el) => running.push(el.animate(leaveFrames, leave))));
        } else {
          running.push(prevRow.animate(leaveFrames, leave));
        }

        const parts = nextItems.map((item) => item.querySelectorAll(PARTS));
        if (parts.some((list) => list.length)) {
          parts.forEach((list) => list.forEach((el) => running.push(el.animate(enterFrames, enter))));
        } else {
          running.push(nextRow.animate(enterFrames, enter));
        }

        // La línea de progreso del panel nuevo aparece con el mismo fundido en vez de saltar
        const bar = next.querySelector('[data-enova-tabs-bar]');
        if (bar) running.push(bar.animate([{ opacity: 0 }, { opacity: 1 }], enter));
      }

      Promise.all(running.map((animation) => animation.finished))
        .then(() => {
          // Si hubo otro cambio mientras tanto, la limpieza la hace ese
          if (transition !== this.transitionId) return;
          // En el mismo paso: los recuadros del nuevo vuelven a ser opacos y el anterior se oculta.
          // hidden en un panel ya preparado solo lo vuelve invisible (sin sacarlo de la maqueta), así
          // que cuesta lo mismo que un estado intermedio y se ahorra un recálculo más.
          next.classList.remove('is-entering');
          if (prev && prev !== next && prev !== this.panels[this.shown]) {
            prev.hidden = true;
            prev.classList.remove('is-leaving');
          }
          this.whenIdle(() => {
            if (transition !== this.transitionId) return;
            running.forEach((animation) => animation.cancel());
            nextRow.style.willChange = '';
            onSettled?.();
          });
        })
        .catch(() => {});
    }

    // Corta un cambio en curso (otro clic a mitad de camino)
    stopTransition() {
      this.running?.forEach((animation) => animation.cancel());
      this.running = [];
      this.panels.forEach((panel, i) => {
        panel.classList.remove('is-leaving', 'is-entering');
        if (this.rows[i]) this.rows[i].style.willChange = '';
      });
    }

    // Tarjetas que se ven en la fila, también las que asoman en los márgenes laterales (la fila
    // llega hasta el borde de la pantalla); las demás no necesitan animarse
    visibleItems(row) {
      const items = Array.from(row.children);
      if (!items.length) return [];
      const base = items[0].offsetLeft;
      const bleed = parseFloat(getComputedStyle(row).paddingLeft) || 0;
      const left = row.scrollLeft - bleed;
      const right = left + row.clientWidth;
      return items.filter((item) => {
        const x = item.offsetLeft - base;
        return x + item.offsetWidth > left + 1 && x < right - 1;
      });
    }

    tilesAligned(row) {
      const [first, second] = row.children;
      if (!first || !second) return true;
      const step = second.offsetLeft - first.offsetLeft;
      const rest = step > 0 ? row.scrollLeft % step : 0;
      return rest < 1 || step - rest < 1;
    }

    whenIdle(callback, timeout = 600) {
      if ('requestIdleCallback' in window) requestIdleCallback(callback, { timeout });
      else setTimeout(callback, 120);
    }

    // Deja un panel listo antes de mostrarlo: maquetado aunque esté oculto (data-enova-ready lo
    // deja invisible sin sacarlo de la maqueta), medido, con sus fotos pedidas al tamaño final y
    // con colores y divisores ya calculados. Se hace con la página cargada, un panel por pausa del
    // navegador, o antes si se apunta a su pestaña.
    preparePanels(signal) {
      this.tabs.forEach((tab, index) => {
        ['pointerenter', 'focus', 'touchstart'].forEach((type) => {
          tab.addEventListener(type, () => {
            this.prepare(this.panels[index]);
            this.warm(this.panels[index]);
          }, { passive: true, signal });
        });
      });
      const queue = () => {
        const panel = this.panels.find((item) => !item.dataset.enovaReady && !item.dataset.enovaPreparing);
        if (!panel || signal.aborted) return;
        this.prepare(panel);
        this.whenIdle(queue, 4000);
      };
      const start = () => this.whenIdle(queue, 4000);
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true, signal });
    }

    prepare(panel) {
      if (!panel || panel.dataset.enovaReady || panel.dataset.enovaPreparing) return;
      panel.dataset.enovaPreparing = 'true';
      // Con «Más vendidos», primero las tarjetas en ese orden
      const sorted = this.sorting?.[this.panels.indexOf(panel)] || Promise.resolve();
      sorted.then(() => {
        panel.dataset.enovaReady = 'true';
        panel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
        requestAnimationFrame(() => {
          this.measure([panel]);
          this.settle(panel);
          this.warm(panel);
        });
      });
    }

    // «Más vendidos» (ajuste de cada pestaña): en el home la colección solo se lee en su orden del
    // admin, así que las tarjetas en orden de más vendidos se piden a la página de la colección
    // (sections/enova-tabs-panel, de a 12). Mientras tanto la fila queda oculta (.is-sorting). Si
    // tarda más de 4 s, se muestra la que vino con la página y la respuesta tardía se descarta,
    // para no cambiar tarjetas a la vista.
    sortRow(row, signal) {
      const url = row?.dataset.enovaSortUrl;
      if (!url) return Promise.resolve();
      const limit = parseInt(row.dataset.enovaLimit, 10) || 12;
      const pages = Math.ceil(limit / 12);
      let settled = false;
      const load = Promise.all(Array.from({ length: pages }, (_, page) => (
        fetch(`${url}&section_id=enova-tabs-panel&page=${page + 1}`, { signal })
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      )))
        .then((pagesHtml) => {
          if (settled) return;
          const items = pagesHtml
            .flatMap((html) => Array.from(new DOMParser().parseFromString(html, 'text/html')
              .querySelectorAll('[data-enova-tabs-panel-items] > .enova-tabs__item')))
            .slice(0, limit);
          if (items.length) row.replaceChildren(...items);
        })
        .catch(() => {});
      const timeout = new Promise((resolve) => setTimeout(resolve, 4000));
      return Promise.race([load, timeout]).then(() => {
        settled = true;
        // Las filas que no se ven se muestran ya; la visible la muestra su entrada
        if (this.rows[this.active] !== row) row.classList.remove('is-sorting');
      });
    }

    // Fotos al tamaño final y tarjetas recalculadas (enova-product-cards.js), solo las de este panel
    settle(panel) {
      this.loadImages(panel);
      document.dispatchEvent(new CustomEvent('enova:cards-visible', { detail: { root: panel } }));
    }

    // El tema carga cada foto al entrar en pantalla (responsive-image): mide la tarjeta, cambia el
    // tamaño pedido y, a veces, descarga otra versión. Dentro del carrusel eso pasaba al deslizar,
    // tarjeta por tarjeta; aquí se hace de una vez con el panel ya maquetado.
    loadImages(panel) {
      panel.querySelectorAll('responsive-image').forEach((image) => {
        if (image.hasAttribute('intersecting') || typeof image.loadImage !== 'function' || !image.clientWidth) return;
        image.disconnectObserver?.();
        image.loadImage();
      });
    }

    // Al apuntar a una pestaña, las primeras fotos de su panel se decodifican antes del clic, para que
    // el primer cuadro de la entrada no tenga que esperarlas
    // (también al preparar el panel). Se vuelve a pedir pasados unos segundos: el navegador descarta
    // lo decodificado de un panel oculto.
    warm(panel) {
      if (!panel || Date.now() - (panel.enovaWarmAt || 0) < 4000) return;
      panel.enovaWarmAt = Date.now();
      panel.querySelectorAll('.enova-tabs__item:nth-child(-n+6) .m-product-card__main-image img').forEach((img) => {
        img.decode?.().catch(() => {});
      });
    }

    // Marca corta: las posiciones de las palabras se miden solo cuando cambia el tamaño de la lista
    // (carga de la fuente, ancho de la ventana), así el clic no mide nada
    initMark() {
      if (!this.mark || !this.list) return;
      const remeasure = () => {
        this.measureTabs();
        this.placeMark(false);
      };
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(remeasure);
        observer.observe(this.list);
        this.abort.signal.addEventListener('abort', () => observer.disconnect());
      } else {
        remeasure();
        window.addEventListener('resize', remeasure, { signal: this.abort.signal });
      }
      document.fonts?.ready.then(remeasure);
    }

    measureTabs() {
      // 16 px de marca; queda 0,42 em por debajo de la palabra, libre de los rasgos que bajan
      const gap = parseFloat(getComputedStyle(this.list).fontSize) * 0.42 - this.mark.offsetHeight;
      const half = this.mark.offsetWidth / 2;
      this.tabGeo = this.tabs.map((tab) => ({
        x: tab.offsetLeft + tab.offsetWidth / 2 - half,
        y: tab.offsetTop + tab.offsetHeight + gap,
        ok: tab.offsetWidth > 0,
      }));
    }

    // Viaja a la palabra activa en 0,6 s y a mitad de camino se estira hasta tres veces su largo
    placeMark(animate) {
      const geo = this.tabGeo?.[this.active];
      if (!this.mark || !geo || !geo.ok) return;
      const moving = animate && this.mark.classList.contains('is-ready') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mark.classList.toggle('is-moving', moving);
      this.mark.style.transform = `translate3d(${geo.x}px, ${geo.y}px, 0)`;
      this.mark.classList.add('is-ready');
      this.stretch?.cancel();
      if (moving) {
        this.stretch = this.mark.firstElementChild.animate(
          [{ transform: 'scaleX(1)' }, { transform: 'scaleX(3)', offset: 0.32 }, { transform: 'scaleX(1)' }],
          { duration: 600, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' }
        );
      }
    }

    select(index) {
      if (index === this.active) return;
      const from = this.active;
      this.active = index;
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.swapLink(from, index);
      this.placeMark(true);

      // La pestaña (color, por CSS) y la marca se mueven en este cuadro; el carrusel cambia en el
      // siguiente. No espera a que se decodifiquen las fotos: en Safari del iPhone eso podía
      // demorar cada toque; se decodifican antes, sin bloquear (warm).
      cancelAnimationFrame(this.swapFrame);
      this.swapFrame = requestAnimationFrame(() => {
        // Si sus tarjetas en orden de más vendidos aún no llegaron, entra cuando llegan
        const row = this.rows[index];
        if (!row?.classList.contains('is-sorting')) {
          this.showPanel(index);
          return;
        }
        this.sorting[index].then(() => {
          if (this.active === index) this.showPanel(index);
        });
      });
    }

    // «Ver todo» de la pestaña: el mismo cambio que el contenido de las tarjetas (el anterior se
    // desvanece en 0,16 s y el nuevo aparece en 0,45 s, con 4 px de desplazamiento).
    swapLink(from, to) {
      this.linkAnims?.forEach((animation) => animation.cancel());
      this.linkAnims = [];
      const prev = this.links[from];
      const next = this.links[to];
      this.links.forEach((link) => {
        link.hidden = link !== next && link !== prev;
        link.style.pointerEvents = '';
      });
      if (!next || prev === next) return;
      if (!prev || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        if (prev) prev.hidden = true;
        return;
      }
      const dir = Math.sign(to - from) || 1;
      prev.style.pointerEvents = 'none';
      const leave = prev.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease-out', fill: 'forwards' });
      const enter = next.animate(
        [{ opacity: 0, transform: `translate3d(${dir * 4}px, 0, 0)` }, { opacity: 1, transform: 'translate3d(0, 0, 0)' }],
        { duration: 450, delay: 90, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'backwards' }
      );
      this.linkAnims = [leave, enter];
      leave.finished
        .then(() => {
          prev.hidden = true;
          prev.style.pointerEvents = '';
          leave.cancel();
        })
        .catch(() => {});
    }

    showPanel(index) {
      if (index !== this.active) return;
      const from = this.shown ?? 0;
      const next = this.panels[index];
      const prev = from !== index ? this.panels[from] : null;
      // Un panel que no alcanzó a prepararse se recalcula cuando termine de entrar
      const fresh = !next.dataset.enovaReady;
      next.dataset.enovaReady = 'true';
      // Un cambio a mitad de otro: se cortan las animaciones y quedan solo estos dos paneles
      this.stopTransition();
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index && panel !== prev;
      });
      this.shown = index;
      next.hidden = false;
      const row = this.rows[index];
      if (row && row.scrollLeft) {
        row.style.scrollBehavior = 'auto';
        row.scrollLeft = 0;
        row.style.scrollBehavior = '';
      }
      row?.classList.remove('is-sorting');
      if (fresh || next.enovaMax === undefined) this.measure([next]);
      else this.sync();
      this.slideIn(next, prev, Math.sign(index - from) || 1, fresh ? () => this.settle(next) : undefined);
    }

    scrollByCard(direction) {
      const row = this.rows[this.active];
      const item = row?.firstElementChild;
      if (!item) return;
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      row.scrollBy({ left: direction * (item.getBoundingClientRect().width + gap) });
    }

    // Medidas de la línea de progreso (ancho del tramo visible y recorrido). Primero se leen todas y
    // después se escriben, para una sola maquetación. Los paneles sin maquetar se saltan.
    measure(panels) {
      const sizes = panels.map((panel) => {
        const row = this.rows[this.panels.indexOf(panel)];
        return row && row.clientWidth ? { panel, width: row.clientWidth, total: row.scrollWidth } : null;
      });
      sizes.forEach((size) => {
        if (!size) return;
        const { panel, width, total } = size;
        const visible = Math.min(width / total, 1);
        panel.enovaMax = total - width;
        panel.enovaTravel = ((1 - visible) / visible) * 100;
        const bar = this.bars[this.panels.indexOf(panel)];
        if (bar) {
          bar.style.width = `${visible * 100}%`;
          bar.style.setProperty('--enova-tabs-travel', `${panel.enovaTravel}%`);
        }
      });
      this.sync();
    }

    // Por cuadro al deslizar: flechas de los extremos y, sin progreso por CSS, la posición de la línea
    sync() {
      const panel = this.panels[this.active];
      const row = this.rows[this.active];
      if (!panel || !row) return;
      const max = panel.enovaMax || 0;
      const left = row.scrollLeft;
      if (!scrollDriven) {
        const bar = this.bars[this.active];
        const offset = max > 0 ? (left / max) * (panel.enovaTravel || 0) : 0;
        if (bar) bar.style.transform = `translate3d(${offset}%, 0, 0)`;
      }
      const atStart = left <= 2;
      const atEnd = left >= max - 2;
      if (this.prev && this.prev.disabled !== atStart) this.prev.disabled = atStart;
      if (this.next && this.next.disabled !== atEnd) this.next.disabled = atEnd;
    }
  }

  customElements.define('enova-product-tabs', EnovaProductTabs);
}
